import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Navbar = () => {
    const { t, i18n } = useTranslation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState(null);

    const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

    const handleDropdownToggle = (dropdown) => {
        setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
    };

    const closeMenu = () => {
        setIsMenuOpen(false);
        setActiveDropdown(null);
    };

    const changeLanguage = (lng) => {
        i18n.changeLanguage(lng);
        localStorage.setItem('i18nextLng', lng);
    };

    return (
        <nav className="bg-white sticky top-0 z-50 shadow-lg">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    {/* Brand Logo */}
                    <Link
                        to="/"
                        className="flex items-center hover:opacity-80 transition-opacity duration-300"
                        onClick={closeMenu}
                    >
                        <img
                            src="/shreelogo.png"
                            alt="Choundeshwari Credit Society"
                            className="h-12 w-auto"
                        />
                    </Link>

                    {/* Mobile Menu Button */}
                    <button
                        onClick={toggleMenu}
                        className="lg:hidden text-gray-700 p-2 rounded-md hover:bg-gray-100 focus:outline-none"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {isMenuOpen ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            )}
                        </svg>
                    </button>

                    {/* Desktop Menu */}
                    <div className="hidden lg:flex items-center space-x-1 whitespace-nowrap">

                        {/* Home */}
                        <Link
                            to="/"
                            className="text-gray-700 px-4 py-2 rounded-md hover:bg-gray-100 hover:text-primary transition-all duration-300"
                        >
                            {t('navbar.home')}
                        </Link>

                        {/* About Us */}
                        <Link
                            to="/about"
                            className="text-gray-700 px-4 py-2 rounded-md hover:bg-gray-100 hover:text-primary transition-all duration-300"
                        >
                            {t('navbar.aboutUs')}
                        </Link>

                        {/* Deposits */}
                        <Link
                            to="/deposits"
                            className="text-gray-700 px-4 py-2 rounded-md hover:bg-gray-100 hover:text-primary transition-all duration-300"
                        >
                            {t('navbar.deposits')}
                        </Link>

                        {/* Loans */}
                        <Link
                            to="/loans"
                            className="text-gray-700 px-4 py-2 rounded-md hover:bg-gray-100 hover:text-primary transition-all duration-300"
                        >
                            {t('navbar.loans')}
                        </Link>

                        {/* Financial Info */}
                        <Link
                            to="/financial-info"
                            className="text-gray-700 px-4 py-2 rounded-md hover:bg-gray-100 hover:text-primary transition-all duration-300"
                        >
                            {t('navbar.financialInfo')}
                        </Link>



                        {/* Future Plans */}
                        <Link
                            to="/future-plans"
                            className="text-gray-700 px-4 py-2 rounded-md hover:bg-gray-100 hover:text-primary transition-all duration-300"
                        >
                            {t('navbar.futurePlans')}
                        </Link>

                        {/* Media */}
                        <Link
                            to="/media"
                            className="text-gray-700 px-4 py-2 rounded-md hover:bg-gray-100 hover:text-primary transition-all duration-300"
                        >
                            {t('navbar.media')}
                        </Link>

                        {/* Careers */}
                        <Link
                            to="/careers"
                            className="text-gray-700 px-4 py-2 rounded-md hover:bg-gray-100 hover:text-primary transition-all duration-300"
                        >
                            {t('navbar.careers')}
                        </Link>

                        {/* Contact */}
                        <Link
                            to="/contact"
                            className="text-gray-700 px-4 py-2 rounded-md hover:bg-gray-100 hover:text-primary transition-all duration-300"
                        >
                            {t('navbar.contact')}
                        </Link>

                        {/* Language Switcher */}
                        <div className="relative group ml-4">
                            <button className="text-gray-700 px-4 py-2 rounded-md hover:bg-gray-100 hover:text-primary transition-all duration-300 flex items-center gap-1">
                                {i18n.language === 'en' ? 'EN' : 'मराठी'}
                                <svg className="w-4 h-4 transition-transform group-hover:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform group-hover:translate-y-0 -translate-y-2">
                                <button
                                    onClick={() => changeLanguage('en')}
                                    className={`block w-full text-left px-4 py-3 rounded-t-lg transition-colors duration-200 ${i18n.language === 'en' ? 'bg-primary/10 text-primary font-semibold' : 'text-gray-700 hover:bg-gray-100 hover:text-primary'}`}
                                >
                                    English
                                </button>
                                <button
                                    onClick={() => changeLanguage('mr')}
                                    className={`block w-full text-left px-4 py-3 rounded-b-lg transition-colors duration-200 ${i18n.language === 'mr' ? 'bg-primary/10 text-primary font-semibold' : 'text-gray-700 hover:bg-gray-100 hover:text-primary'}`}
                                >
                                    मराठी
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            <div className={`lg:hidden ${isMenuOpen ? 'block' : 'hidden'} bg-white border-t border-gray-200`}>
                <div className="px-2 pt-2 pb-3 space-y-1">
                    <Link to="/" onClick={closeMenu} className="block px-3 py-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors duration-200">
                        {t('navbar.home')}
                    </Link>

                    <Link to="/about" onClick={closeMenu} className="block px-3 py-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors duration-200">
                        {t('navbar.aboutUs')}
                    </Link>

                    <Link to="/deposits" onClick={closeMenu} className="block px-3 py-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors duration-200">
                        {t('navbar.deposits')}
                    </Link>

                    <Link to="/loans" onClick={closeMenu} className="block px-3 py-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors duration-200">
                        {t('navbar.loans')}
                    </Link>

                    <Link to="/financial-info" onClick={closeMenu} className="block px-3 py-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors duration-200">
                        {t('navbar.financialInfo')}
                    </Link>



                    <Link to="/future-plans" onClick={closeMenu} className="block px-3 py-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors duration-200">
                        {t('navbar.futurePlans')}
                    </Link>

                    <Link to="/media" onClick={closeMenu} className="block px-3 py-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors duration-200">
                        {t('navbar.media')}
                    </Link>

                    <Link to="/careers" onClick={closeMenu} className="block px-3 py-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors duration-200">
                        {t('navbar.careers')}
                    </Link>

                    <Link to="/contact" onClick={closeMenu} className="block px-3 py-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors duration-200">
                        {t('navbar.contact')}
                    </Link>

                    {/* Mobile Language Switcher */}
                    <div className="pt-4 border-t border-gray-200">
                        <button
                            onClick={() => changeLanguage('en')}
                            className={`block w-full text-left px-3 py-2 rounded-md transition-colors duration-200 ${i18n.language === 'en' ? 'bg-primary/10 text-primary font-semibold' : 'text-gray-700 hover:bg-gray-100'}`}
                        >
                            English
                        </button>
                        <button
                            onClick={() => changeLanguage('mr')}
                            className={`block w-full text-left px-3 py-2 rounded-md transition-colors duration-200 ${i18n.language === 'mr' ? 'bg-primary/10 text-primary font-semibold' : 'text-gray-700 hover:bg-gray-100'}`}
                        >
                            मराठी
                        </button>
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;
